import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { Box, Typography, Stack, styled, Divider, ThemeProvider } from '@mui/material'
import { ReactComponent as StartDelivery } from './Assets/StartDelivery.svg'
import { ReactComponent as DeliveryOnProcess } from './Assets/DeliveryOnProcess.svg'
import { ReactComponent as SuccessDelivery } from './Assets/SuccessDelivery.svg'
import StoreTranslate from 'GlobalStores/Cores/Translator/StoreTranslate'
import { useNavigate } from 'react-router-dom'
import { useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import { MainTemplate } from 'Components/Template/MainTemplate'
import { get } from 'lodash'
import moment from 'moment'

const OrderTracking = observer(({ dataTracking }) => {
    const translate = useTranslate()
    const navigate = useNavigate()

    const [dataItems, setDataItems] = useState([])

    useEffect(() => {
        if (dataTracking) {
            setDataItems(dataTracking.items.reverse())
        }
    }, [dataTracking])

    const formatDate = dateStr => {
        return moment(dateStr)
            .locale(StoreTranslate.CurrentLanguage() === 'TH' ? 'th-TH' : 'en-GB')
            .format('DD-MMM-YYYY HH:mm A')
    }

    return (
        <ThemeProvider theme={MainTemplate}>
            <TypographyStyled fontWeight={700} fontSize={'32px !important'} marginBottom={'32px'} color={'#003B6F'}>
                {translate('order_tracking')}
            </TypographyStyled>
            <Stack
                sx={{
                    borderBottom: '1px solid #CCDEEF',
                    paddingBottom: '32px',
                    flexDirection: 'column',
                    '@media screen and (min-width: 530px)': {
                        flexDirection: 'row'
                    }
                }}>
                <TypographyStyled fontWeight={400} color={'#003B6F'} fontSize={'20px !important'}>
                    {translate('tracking_number')}:&nbsp;
                </TypographyStyled>
                <TypographyStyled fontWeight={400} fontSize={'20px !important'} color={'#5A8FC3'} sx={{ textDecorationLine: 'underline' }}>
                    {get(dataTracking, 'items[0].doNo', 0)}
                </TypographyStyled>
            </Stack>
            <Stack
                sx={{
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: '42px 0px',
                    flexDirection: 'column',
                    width: 'fit-content',
                    '@media screen and (min-width: 600px)': {
                        padding: '42px 20px 20px 20px',
                        margin: '0 auto'
                    }
                }}>
                {
                    dataItems.map((item, index) => {
                        const { isLatestStep, isFirstStep } = item

                        return (
                            <Stack
                                key={index}
                                sx={{
                                    flexDirection: 'row',
                                    alignItems: isLatestStep ? 'flex-start' : 'center',
                                    justifyContent: 'center',
                                    width: 'fit-content',
                                    margin: '-1px 0px'
                                }}
                                className={isLatestStep ? 'latest-step' : isFirstStep ? 'first-step' : ''}>
                                <Stack
                                    padding={isLatestStep ? '12px 24px 0px 24px' : isFirstStep ? '0px 24px 45px 24px' : '0px 24px'}
                                    sx={{
                                        padding: isLatestStep ? '12px 20px 0px 15px' : isFirstStep ? '0px 20px 45px 15px' : '0px 20px 0px 15px',
                                        '@media screen and (min-width: 600px)': {
                                            padding: isLatestStep ? '12px 24px 0px 24px' : isFirstStep ? '0px 24px 45px 24px' : '0px 24px'
                                        }
                                    }}>
                                    {isLatestStep === false ? (
                                        <Divider
                                            id="upperLine"
                                            sx={{
                                                borderLeft: '2px dashed #ccc',
                                                minHeight: '48px',
                                                background: 'none',
                                                margin: '-1px auto',
                                                '@media screen and (min-width: 600px)': {
                                                    margin: '0 auto',
                                                    minHeight: '40px'
                                                }
                                            }}
                                        />
                                    ) : null}

                                    <Stack minWidth={'35px'}>
                                        {isLatestStep && (
                                            <SuccessDelivery
                                                style={{
                                                    width: '35px',
                                                    height: '35px'
                                                }}
                                            />
                                        )}
                                        {!isFirstStep && !isLatestStep && (
                                            <DeliveryOnProcess
                                                style={{
                                                    width: '35px',
                                                    height: '35px'
                                                }}
                                            />
                                        )}
                                        {isFirstStep && (
                                            <StartDelivery
                                                style={{
                                                    width: '35px',
                                                    height: '35px'
                                                }}
                                            />
                                        )}
                                    </Stack>

                                    {!isFirstStep && (
                                        <Divider
                                            id="lowerLine"
                                            sx={{
                                                borderLeft: '2px dashed #ccc',
                                                minHeight: '48px',
                                                background: 'none',
                                                margin: '-1px auto',
                                                '@media screen and (min-width: 600px)': {
                                                    margin: '0 auto',
                                                    minHeight: '40px'
                                                }
                                            }}
                                        />
                                    )}
                                </Stack>
                                <Stack
                                    sx={{
                                        paddingTop: isLatestStep ? '6px' : '0px'
                                    }}>
                                    <TypographyStyled sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }} fontWeight={400} fontSize={'16px !important'} color={isLatestStep ? '#003B6F' : '#8A9BB0'}>
                                        {StoreTranslate.CurrentLanguage() === 'EN' ? item.trackDate.english : item.trackDate.native}
                                        <TypographyStyled fontWeight={400} fontSize={'16px !important'} color={'#8A9BB0'}>
                                            {StoreTranslate.CurrentLanguage() === 'EN' ? item.trackTime.english : item.trackTime.native}
                                        </TypographyStyled>
                                    </TypographyStyled>
                                    <TypographyStyled fontWeight={700} fontSize={'20px !important'} color={isLatestStep ? '#003B6F' : '#8A9BB0'}>
                                        {StoreTranslate.UseLanguage(item.descriptionStatus)}
                                    </TypographyStyled>
                                    {item.dc && (
                                        <TypographyStyled fontWeight={400} fontSize={'16px !important'} color={isLatestStep ? '#003B6F' : '#8A9BB0'}>
                                            ● {item.dc}
                                        </TypographyStyled>
                                    )}
                                </Stack>
                            </Stack>
                        )
                    })}
            </Stack>
            <Box textAlign="center" borderTop={'1px solid #CCDEEF'} paddingTop={'32px'}>
                <TypographyStyled
                    onClick={() => navigate(-1)}
                    fontSize={'16px !important'}
                    fontWeight={400}
                    color="#003B6F"
                    sx={{
                        cursor: 'pointer',
                        textDecorationLine: 'underline'
                    }}>
                    {translate('back')}
                </TypographyStyled>
            </Box>
        </ThemeProvider>
    )
})

const TypographyStyled = styled(Typography)`
    /* font-family: Inter, Prompt; */
    /* font-family: Prompt, sans-serif; */
`

export default OrderTracking
